import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  Mail,
  Phone,
  MapPin,
  Clock,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  CONTACT,
  SERVICE_OPTIONS,
  BUDGET_OPTIONS,
} from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  company: "",
  service: "",
  budget: "",
  message: "",
  consent: true,
};

const Contact = ({ prefill }) => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (prefill?.service) {
      setForm((f) => ({ ...f, service: prefill.service }));
    }
  }, [prefill]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Te rugăm să completezi numele, emailul și mesajul.");
      return;
    }
    if (!form.consent) {
      toast.error("Te rugăm să accepți politica de confidențialitate.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Cererea ta a fost trimisă. Revenim în maxim 24h.");
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail || "A apărut o eroare. Încearcă din nou.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-20 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">
              <span className="h-px w-8 bg-electric" />
              Contact
            </div>
            <h2 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900">
              Hai să <span className="text-magenta">vorbim</span>.
            </h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              Completează formularul și revenim în maxim 24h cu o primă propunere de discuție.
              Fără pitch-uri lungi, fără presiune.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 p-4 rounded-2xl border border-slate-200 bg-white hover:border-magenta/40 transition-colors group"
              >
                <span className="h-11 w-11 rounded-xl bg-[#E91E8B]/10 text-magenta grid place-items-center">
                  <Mail size={20} />
                </span>
                <div>
                  <div className="text-xs text-slate-500 font-medium">Email</div>
                  <div className="font-semibold text-slate-900 group-hover:text-magenta transition-colors">
                    {CONTACT.email}
                  </div>
                </div>
              </a>

              <a
                href={`tel:${CONTACT.phoneRaw}`}
                data-testid="contact-phone-link"
                className="flex items-center gap-4 p-4 rounded-2xl border border-slate-200 bg-white hover:border-magenta/40 transition-colors group"
              >
                <span className="h-11 w-11 rounded-xl bg-[#2962FF]/10 text-electric grid place-items-center">
                  <Phone size={20} />
                </span>
                <div>
                  <div className="text-xs text-slate-500 font-medium">Telefon</div>
                  <div className="font-semibold text-slate-900 group-hover:text-magenta transition-colors">
                    {CONTACT.phone}
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 rounded-2xl border border-slate-200 bg-white">
                <span className="h-11 w-11 rounded-xl bg-[#2962FF]/10 text-electric grid place-items-center">
                  <MapPin size={20} />
                </span>
                <div>
                  <div className="text-xs text-slate-500 font-medium">Locație</div>
                  <div className="font-semibold text-slate-900">București, România</div>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-2xl border border-slate-200 bg-white">
                <span className="h-11 w-11 rounded-xl bg-[#E91E8B]/10 text-magenta grid place-items-center">
                  <Clock size={20} />
                </span>
                <div>
                  <div className="text-xs text-slate-500 font-medium">Program</div>
                  <div className="font-semibold text-slate-900">Luni - Vineri · 09:00 - 18:00</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative rounded-3xl border border-slate-200 bg-[#FAFAFA] p-6 lg:p-10">
              {done ? (
                <div data-testid="contact-success" className="text-center py-12">
                  <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 grid place-items-center">
                    <CheckCircle2 size={36} className="text-emerald-600" />
                  </div>
                  <h3 className="font-display mt-6 text-3xl font-bold text-slate-900">
                    Mulțumim!
                  </h3>
                  <p className="mt-3 text-slate-600 max-w-md mx-auto">
                    Am primit cererea ta. Un strateg C2 te va contacta în maxim 24 de ore lucrătoare.
                  </p>
                  <Button
                    onClick={() => setDone(false)}
                    variant="outline"
                    className="mt-6 rounded-full"
                    data-testid="contact-reset-button"
                  >
                    Trimite altă cerere
                  </Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-sm font-semibold text-slate-800">
                        Nume complet <span className="text-magenta">*</span>
                      </Label>
                      <Input
                        id="name"
                        data-testid="contact-input-name"
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        placeholder="Ion Popescu"
                        className="mt-1.5 h-11 input-brand bg-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm font-semibold text-slate-800">
                        Email <span className="text-magenta">*</span>
                      </Label>
                      <Input
                        id="email"
                        data-testid="contact-input-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="ion@firma.ro"
                        className="mt-1.5 h-11 input-brand bg-white"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone" className="text-sm font-semibold text-slate-800">
                        Telefon
                      </Label>
                      <Input
                        id="phone"
                        data-testid="contact-input-phone"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="+40 7XX XXX XXX"
                        className="mt-1.5 h-11 input-brand bg-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company" className="text-sm font-semibold text-slate-800">
                        Companie
                      </Label>
                      <Input
                        id="company"
                        data-testid="contact-input-company"
                        value={form.company}
                        onChange={(e) => update("company", e.target.value)}
                        placeholder="Firma SRL"
                        className="mt-1.5 h-11 input-brand bg-white"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-semibold text-slate-800">
                        Serviciu de interes
                      </Label>
                      <Select
                        value={form.service}
                        onValueChange={(v) => update("service", v)}
                      >
                        <SelectTrigger
                          data-testid="contact-select-service"
                          className="mt-1.5 h-11 bg-white"
                        >
                          <SelectValue placeholder="Alege un serviciu" />
                        </SelectTrigger>
                        <SelectContent>
                          {SERVICE_OPTIONS.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-slate-800">
                        Buget estimativ
                      </Label>
                      <Select
                        value={form.budget}
                        onValueChange={(v) => update("budget", v)}
                      >
                        <SelectTrigger
                          data-testid="contact-select-budget"
                          className="mt-1.5 h-11 bg-white"
                        >
                          <SelectValue placeholder="Alege un interval" />
                        </SelectTrigger>
                        <SelectContent>
                          {BUDGET_OPTIONS.map((b) => (
                            <SelectItem key={b} value={b}>
                              {b}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-sm font-semibold text-slate-800">
                      Mesaj <span className="text-magenta">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder="Spune-ne pe scurt despre business, obiective și ce ai încercat până acum…"
                      rows={5}
                      className="mt-1.5 input-brand bg-white resize-none"
                      required
                    />
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="consent"
                      data-testid="contact-checkbox-consent"
                      checked={form.consent}
                      onCheckedChange={(v) => update("consent", !!v)}
                      className="mt-1 data-[state=checked]:bg-magenta data-[state=checked]:border-magenta"
                    />
                    <Label
                      htmlFor="consent"
                      className="text-sm text-slate-600 leading-relaxed font-normal cursor-pointer"
                    >
                      Sunt de acord ca datele să fie folosite exclusiv pentru a fi contactat în legătură
                      cu cererea mea. Nu trimitem spam.
                    </Label>
                  </div>

                  <Button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={submitting}
                    className="w-full sm:w-auto rounded-full bg-magenta hover:bg-[#C2185B] text-white h-12 px-8 text-base font-semibold disabled:opacity-70"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={18} className="mr-2 animate-spin" />
                        Se trimite…
                      </>
                    ) : (
                      <>
                        Trimite cererea
                        <ArrowRight size={18} className="ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
